import { useLogger } from "amazon-chime-sdk-component-library-react";
import { useBrowserCompatibilityFeatures } from "@platform/features/video/hooks/useBrowserCompatibilityFeatures";
import { useDevices } from "../../../providers/DevicesProvider";
import DeviceInput from "../DeviceInput";

interface Props {
  /** The message that will be shown when no audio output speaker devices are found. */
  notFoundMsg?: string;
  /** The label that will be shown for speaker selection, it defaults to `Speaker source`. */
  label?: string;
  /** The callback fired when the selection is changed.
   *  It is required if you want to add testing functionality around speaker selection. */
  onChange?: (selectedAudioOutputDevice: string) => void;
  /** Custom devices to use instead of the ones from useDevices hook */
  devices?: MediaDeviceInfo[];
  /** Custom selected device */
  selectedDevice?: string | null;
}

export const SpeakerSelection: React.FC<React.PropsWithChildren<Props>> = ({
  notFoundMsg = "No speaker devices found",
  label = "Speaker source",
  onChange,
  devices: customDevices,
  selectedDevice: customSelectedDevice,
  ...rest
}) => {
  const logger = useLogger();

  const {
    audioOutputDevices,
    selectedAudioOutputDevice,
    selectAudioOutputDevice,
  } = useDevices();

  const { supportsAudioOutput } = useBrowserCompatibilityFeatures();

  // Use custom devices if provided, otherwise use the ones from the hook
  const effectiveDevices = customDevices !== undefined ? customDevices : audioOutputDevices;
  const effectiveSelectedDevice = customSelectedDevice !== undefined ? customSelectedDevice : selectedAudioOutputDevice;

  const handleSelect = async (deviceId: string): Promise<void> => {
    try {
      // Only call the hook's selectAudioOutputDevice if we're not using custom devices
      if (customDevices === undefined) {
        await selectAudioOutputDevice(deviceId);
      }
      onChange && onChange(deviceId);
    } catch (error) {
      logger.error("SpeakerSelection failed to select speaker");
    }
  };

  return (
    <DeviceInput
      name="speaker-selection"
      label={label}
      devices={effectiveDevices}
      onChange={handleSelect}
      selectedDevice={effectiveSelectedDevice}
      disabled={!supportsAudioOutput}
      notFoundMsg={notFoundMsg}
      {...rest}
    />
  );
};

export default SpeakerSelection;
